import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import CodeBlock from './CodeBlock.jsx';
import MermaidDiagram from './MermaidDiagram.jsx';
import styles from './MarkdownRenderer.module.css';

// Extract text recursively from a React node
function extractText(node) {
  if (node === null || node === undefined) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node.props?.children) return extractText(node.props.children);
  return '';
}

// GitHub Alert icons
function AlertIcon({ type }) {
  switch (type) {
    case 'NOTE':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm8-6.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM6.5 7.75A.75.75 0 0 1 7.25 7h1a.75.75 0 0 1 .75.75v2.75h.25a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h.25v-2h-.25a.75.75 0 0 1-.75-.75ZM8 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
        </svg>
      );
    case 'TIP':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1.5c-2.363 0-4 1.69-4 3.75 0 .984.424 1.625.984 2.304l.214.253c.223.264.47.556.673.848.284.411.537.896.621 1.49a.75.75 0 0 1-1.484.211c-.04-.282-.163-.547-.37-.847a8.456 8.456 0 0 0-.542-.68c-.684-.816-1.596-1.9-1.596-3.579C2.5 2.456 4.887 0 8 0s5.5 2.456 5.5 5.25c0 1.679-.912 2.763-1.596 3.58a8.45 8.45 0 0 0-.542.679c-.207.3-.33.565-.37.847a.75.75 0 0 1-1.484-.212c.084-.593.337-1.078.621-1.489.203-.292.45-.584.673-.848.075-.088.147-.173.213-.253.561-.679.985-1.32.985-2.304 0-2.06-1.637-3.75-4-3.75ZM5.75 12h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5ZM6.5 14.5h3a.75.75 0 0 1 0 1.5h-3a.75.75 0 0 1 0-1.5Z" />
        </svg>
      );
    case 'IMPORTANT':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v9.5A1.75 1.75 0 0 1 14.25 13H9.06l-2.573 2.573A1.458 1.458 0 0 1 4 14.543V13H1.75A1.75 1.75 0 0 1 0 11.25Zm1.75-.25a.25.25 0 0 0-.25.25v9.5c0 .138.112.25.25.25h3a.75.75 0 0 1 .75.75v2.19l2.72-2.72a.749.749 0 0 1 .53-.22h5.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25Zm6.25 2a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 3.5ZM9 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </svg>
      );
    case 'WARNING':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </svg>
      );
    case 'CAUTION':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M4.47.22A.749.749 0 0 1 5 0h6c.199 0 .389.079.53.22l4.25 4.25c.141.14.22.331.22.53v6a.749.749 0 0 1-.22.53l-4.25 4.25A.749.749 0 0 1 11 16H5a.749.749 0 0 1-.53-.22L.22 11.53A.749.749 0 0 1 0 11V5c0-.199.079-.389.22-.53Zm.84 1.28L1.5 5.31v5.38l3.81 3.81h5.38l3.81-3.81V5.31L10.69 1.5ZM8 4a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
        </svg>
      );
    default:
      return null;
  }
}

// Custom blockquote renderer that detects [!NOTE], [!TIP], etc.
function renderBlockquote({ children }) {
  let alertType = null;
  let cleanChildren = children;

  try {
    const firstChild = Array.isArray(children) ? children[0] : children;
    if (firstChild?.props?.children) {
      const pChildren = firstChild.props.children;
      let textToCheck = '';
      if (typeof pChildren === 'string') {
        textToCheck = pChildren;
      } else if (Array.isArray(pChildren) && typeof pChildren[0] === 'string') {
        textToCheck = pChildren[0];
      }

      const match = textToCheck.match(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*(.*)$/i);
      if (match) {
        alertType = match[1].toUpperCase();
        const remaining = match[2];
        const newPChildren = Array.isArray(pChildren)
          ? [remaining, ...pChildren.slice(1)]
          : remaining;

        cleanChildren = [
          <p key="first-p" {...firstChild.props}>
            {newPChildren}
          </p>,
          ...(Array.isArray(children) ? children.slice(1) : []),
        ];
      }
    }
  } catch {
    // Fallback if parsing fails
  }

  if (alertType) {
    const alertClasses = {
      NOTE: styles.alertNote,
      TIP: styles.alertTip,
      IMPORTANT: styles.alertImportant,
      WARNING: styles.alertWarning,
      CAUTION: styles.alertCaution,
    };

    const alertTitles = {
      NOTE: 'Note',
      TIP: 'Tip',
      IMPORTANT: 'Important',
      WARNING: 'Warning',
      CAUTION: 'Caution',
    };

    return (
      <aside className={`${styles.alert} ${alertClasses[alertType] || ''}`}>
        <div className={styles.alertHeader}>
          <AlertIcon type={alertType} />
          <span className={styles.alertTitle}>{alertTitles[alertType]}</span>
        </div>
        <div className={styles.alertBody}>{cleanChildren}</div>
      </aside>
    );
  }

  return <blockquote className={styles.blockquote}>{children}</blockquote>;
}

// Load all static content media assets eagerly at build time
const contentAssets = import.meta.glob(
  '/src/content/**/*.{png,jpg,jpeg,gif,svg,webp,avif,ico}',
  { eager: true, import: 'default' },
);

export default function MarkdownRenderer({ content, baseDir = '', className = '' }) {
  return (
    <div className={`${styles.markdown} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code({ inline, className: codeClass, children, ...props }) {
            const match = /language-(\w+)/.exec(codeClass || '');
            const rawText = extractText(children).replace(/\n$/, '');

            if (!inline && match && match[1] === 'mermaid') {
              return <MermaidDiagram chart={rawText} />;
            }

            if (!inline && match) {
              return <CodeBlock language={match[1]} code={rawText} {...props} />;
            }

            if (!inline) {
              return <CodeBlock language="text" code={rawText} {...props} />;
            }

            return (
              <code className={styles.inlineCode} {...props}>
                {children}
              </code>
            );
          },
          pre({ children }) {
            return <>{children}</>;
          },
          img({ src, alt, ...props }) {
            let resolvedSrc = src;
            if (src && !/^(https?:|data:|\/\/)/i.test(src)) {
              const cleanSrc = src.replace(/^\.\//, '');
              if (baseDir) {
                const directKey = `${baseDir}/${cleanSrc}`;
                const inAssetsKey = `${baseDir}/assets/${cleanSrc}`;
                if (contentAssets[directKey]) {
                  resolvedSrc = contentAssets[directKey];
                } else if (contentAssets[inAssetsKey]) {
                  resolvedSrc = contentAssets[inAssetsKey];
                }
              }
            }

            return (
              <img
                src={resolvedSrc}
                alt={alt || ''}
                loading="lazy"
                className={styles.image}
                {...props}
              />
            );
          },
          table({ children, ...props }) {
            return (
              <div className={styles.tableWrapper}>
                <table className={styles.table} {...props}>
                  {children}
                </table>
              </div>
            );
          },
          blockquote: renderBlockquote,
          a({ href, children, ...props }) {
            const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
            return (
              <a
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noreferrer' : undefined}
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
